/**
 * El calendario de campañas (`/activa/campanas/calendario`, fase 3 del plan de campañas, 29/10/2026): lo programado y lo
 * enviado de todos los canales, por mes o por semana, con la fatiga de la marca a la vista. Son las mismas campañas que la
 * lista (`GET /activa/campanas/calendario`), y cada una lleva a su detalle con el mismo link.
 *
 * - Arriba, la regla de fatiga de la marca («Tu marca: 1 mensaje cada 7 días por persona») y, si dos campañas de WhatsApp
 *   salen más cerca que eso, el aviso con las dos, que además quedan marcadas en el calendario.
 * - Mes o semana, con flechas y «Hoy», y el filtro de canal: todo en la URL.
 * - Cada campaña, en sus días: el color de su tipo y el ícono de su canal, el nombre, cuántas personas y el estado. Una del
 *   oficial que sale en tandas aparece en cada día de su plan; la próxima repetición de una que se repite, en el suyo.
 * - Una persona de un cliente ve lo mismo que en la lista: sin las campañas con segmentación (el backend ni las lee).
 *
 * En el celular (menos de `md`) el mes y la semana son una lista por día: la grilla de siete columnas no entra.
 */

import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronLeft, ChevronRight, Mail, MessageCircle, PauseCircle, Repeat, Send, XCircle, type LucideIcon } from "lucide-react";
import { Aviso, EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { usePedido } from "../../emails/hooks/usePedido";
import { btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { campanasApi } from "../api";
import { VistaCampanas } from "../components/VistaCampanas";
import {
  DIAS_SEMANA,
  VISTA,
  VISTAS,
  advertenciasDeFatiga,
  claveDia,
  deClave,
  enviosDelCalendario,
  moverVista,
  porDia,
  rangoDeVista,
  reglaDeFatiga,
  textoDelDia,
  tituloDeVista,
  tituloDelDia,
  urlDeLaVista,
  vistaDeLaUrl,
  type EnvioDelDia,
  type EstadoVista,
  type RangoVista,
} from "../logic/calendario";
import { CANAL, CANALES, ESTADO, MUNDO, MUNDOS } from "../logic/campanas";
import type { Calendario, Canal, EstadoCampana, Mundo } from "../types";

/** En una celda del mes entran tres; las demás, con «+N más», en la semana de ese día. */
export const MAX_EN_CELDA = 3;

const chip = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold";
const flecha =
  "min-h-[44px] min-w-[44px] rounded-xl border border-slate-200 bg-white inline-flex items-center justify-center text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";
const CERCA = "sale cerca de otra campaña de WhatsApp";

const ICONO_CANAL: Record<Canal, LucideIcon> = { whatsapp: MessageCircle, mail: Mail };
const ICONO_ESTADO: Record<EstadoCampana, LucideIcon> = {
  borrador: CalendarClock,
  programada: CalendarClock,
  enviando: Send,
  pausada: PauseCircle,
  terminada: CheckCircle2,
  cancelada: XCircle,
};
const PROXIMA = { texto: "Se repite", cls: "bg-violet-50 text-violet-700" };

const capital = (s: string) => `${s[0].toUpperCase()}${s.slice(1)}`;

/** Todo lo de una campaña en su día, en una línea: lo que lee un lector de pantalla en la celda del mes. */
function descripcion(x: EnvioDelDia, marcada: boolean): string {
  const c = x.envio.campana;
  const sale = x.envio.proxima || c.estado === "programada";
  return [
    `${c.nombre || "Sin nombre"}${x.envio.proxima ? " (próxima repetición)" : ""}`,
    MUNDO[c.mundo].label,
    (x.envio.proxima ? PROXIMA : ESTADO[c.estado]).texto,
    x.dia.hora ? `${sale ? "sale a las" : "desde las"} ${x.dia.hora}` : null,
    textoDelDia(x),
    marcada ? CERCA : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function CalendarioCampanasPage() {
  const equipo = useEsEquipoDeenex();
  const [sp, setSp] = useSearchParams();
  const hoy = useMemo(() => new Date(), []);
  const v = useMemo(() => vistaDeLaUrl(sp, hoy), [sp, hoy]);
  const rango = useMemo(() => rangoDeVista(v.vista, v.dia), [v.vista, v.dia]);
  const pedido = usePedido<Calendario>(() => campanasApi.calendario(rango), `calendario?${rango.dias[0]}/${rango.dias[rango.dias.length - 1]}`);
  const datos = pedido.dato;
  // los tipos que ve quien mira: los dice el backend; mientras tanto, por su rol (un cliente no ve las con segmentación)
  const mundos: Mundo[] = datos?.mundos ?? MUNDOS.filter((m) => equipo || m !== "segmentacion");

  const envios = useMemo(() => enviosDelCalendario(datos?.campanas ?? [], hoy), [datos, hoy]);
  const dias = useMemo(() => porDia(envios, rango.dias, v.canal), [envios, rango, v.canal]);
  // la fatiga es de WhatsApp: con el filtro en mail no hay nada que avisar
  const advertencias = useMemo(
    () => (v.canal === "mail" ? [] : (datos?.fatiga.maximo ?? 1) > 1 ? [] : advertenciasDeFatiga(envios, datos?.fatiga.dias ?? 0, new Set(rango.dias))),
    [envios, datos, rango, v.canal],
  );
  const marcadas = useMemo(() => new Set(advertencias.flatMap((a) => [a.a.clave, a.b.clave])), [advertencias]);

  const ir = (cambios: Partial<EstadoVista>) => setSp(urlDeLaVista({ ...v, ...cambios }, hoy), { replace: true });
  const hoyDia = claveDia(hoy);
  const mes = (deClave(v.dia) ?? hoy).getMonth();
  const regla = datos ? reglaDeFatiga(datos.fatiga.dias, datos.fatiga.maximo ?? 1) : null;
  const vacio = ![...dias.values()].some((xs) => xs.length > 0);

  return (
    <Pagina>
      <EncabezadoPagina
        antetitulo="Campañas"
        titulo="Calendario de campañas"
        bajada="Lo programado y lo enviado de todos los canales, día por día, con la fatiga de tu marca a la vista."
        acciones={<VistaCampanas actual="calendario" canal={v.canal} />}
      />

      {regla && (
        <Aviso tono="info" titulo={regla.titulo} testId="regla-fatiga">
          {regla.detalle}
        </Aviso>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="controles-calendario">
        <div role="group" aria-label="Mes o semana" className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
          {VISTAS.map((x) => (
            <button
              key={x}
              type="button"
              aria-pressed={v.vista === x}
              onClick={() => ir({ vista: x })}
              className={`min-h-[44px] px-4 rounded-lg text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                v.vista === x ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {VISTA[x].texto}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" aria-label={VISTA[v.vista].anterior} onClick={() => ir({ dia: moverVista(v.vista, v.dia, -1) })} className={flecha}>
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => ir({ dia: hoyDia })} className={btnSecundario}>
            Hoy
          </button>
          <button type="button" aria-label={VISTA[v.vista].siguiente} onClick={() => ir({ dia: moverVista(v.vista, v.dia, 1) })} className={flecha}>
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
        <h2 className="min-w-0 text-lg font-semibold text-slate-900" data-testid="titulo-vista">
          {tituloDeVista(v.vista, v.dia)}
        </h2>
        <div role="group" aria-label="Canal" className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
          <BotonFiltro activo={!v.canal} onClick={() => ir({ canal: undefined })}>
            Todos los canales
          </BotonFiltro>
          {CANALES.map((c) => (
            <BotonFiltro key={c} activo={v.canal === c} onClick={() => ir({ canal: c })}>
              {CANAL[c]}
            </BotonFiltro>
          ))}
        </div>
      </div>

      {advertencias.length > 0 && (
        <Aviso tono="atencion" titulo={`Ojo con la fatiga: ${num(advertencias.length)} ${plural(advertencias.length, "campaña sale", "campañas salen")} muy cerca de otra`} testId="advertencias-fatiga">
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {advertencias.map((a) => (
              <li key={a.clave}>{a.texto}</li>
            ))}
          </ul>
        </Aviso>
      )}

      {(datos?.avisos ?? []).map((a) => (
        <Aviso key={`${a.mundo}-${a.codigo}`} tono={a.codigo === "NO_DISPONIBLE" ? "error" : "atencion"} testId={`aviso-${a.mundo}`}>
          {a.texto}
        </Aviso>
      ))}

      {pedido.error && !datos ? (
        <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />
      ) : !datos ? (
        <Esqueleto alto="h-96" etiqueta="Cargando el calendario" />
      ) : (
        <>
          {pedido.error && <p className="text-xs text-amber-700">{pedido.error} Se muestra lo último que trajimos.</p>}
          <Leyenda mundos={mundos} />
          {vacio && (
            <p className="hidden text-sm text-slate-500 md:block" data-testid="calendario-vacio">
              {VISTA[v.vista].vacio}
            </p>
          )}
          {v.vista === "mes" ? (
            <GrillaMes rango={rango} dias={dias} mes={mes} hoy={hoyDia} marcadas={marcadas} verSemana={(dia) => ir({ vista: "semana", dia })} />
          ) : (
            <GrillaSemana rango={rango} dias={dias} hoy={hoyDia} marcadas={marcadas} equipo={equipo} />
          )}
          {/* en el celular, el mes son sus días con algo (sin los de las puntas, que son del mes de al lado); la semana, sus siete */}
          <ListaPorDia
            dias={v.vista === "mes" ? rango.dias.filter((d) => deClave(d)?.getMonth() === mes && (dias.get(d) ?? []).length > 0) : rango.dias}
            deCadaDia={dias}
            hoy={hoyDia}
            marcadas={marcadas}
            equipo={equipo}
            vacio={VISTA[v.vista].vacio}
          />
        </>
      )}
    </Pagina>
  );
}

function BotonFiltro({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={`min-h-[44px] rounded-full border px-3.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
        activo ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

/** Qué es cada color: el tipo de campaña, como los chips de la lista. */
function Leyenda({ mundos }: { mundos: Mundo[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600" aria-label="Qué es cada color" data-testid="leyenda">
      {mundos.map((m) => (
        <li key={m} className="inline-flex items-center gap-1.5">
          <span className={`h-3 w-3 rounded-sm ring-1 ring-inset ring-black/10 ${MUNDO[m].cls}`} aria-hidden="true" />
          {MUNDO[m].label}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <AlertTriangle size={12} className="text-amber-600" aria-hidden="true" />
        Sale cerca de otra (fatiga)
      </li>
    </ul>
  );
}

function IconosCanal({ canales, size }: { canales: Canal[]; size: number }) {
  return (
    <>
      {canales.map((k) => {
        const Icono = ICONO_CANAL[k];
        return <Icono key={k} size={size} className="shrink-0" aria-hidden="true" />;
      })}
    </>
  );
}

/** El número del día; hoy, resaltado. */
function NumeroDelDia({ dia, hoy, apagado = false }: { dia: string; hoy: string; apagado?: boolean }) {
  const n = deClave(dia)?.getDate();
  if (dia === hoy) return <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-violet-600 px-1.5 text-xs font-bold text-white">{n}</span>;
  return <span className={`text-xs font-semibold ${apagado ? "text-slate-400" : "text-slate-700"}`}>{n}</span>;
}

/** Una campaña en una celda del mes: una línea, con todo en la etiqueta (y en el `title`). */
function ChipEnvio({ x, marcada }: { x: EnvioDelDia; marcada: boolean }) {
  const c = x.envio.campana;
  const Estado = x.envio.proxima ? Repeat : ICONO_ESTADO[c.estado];
  const texto = descripcion(x, marcada);
  return (
    <Link
      to={c.link}
      aria-label={texto}
      title={texto}
      data-testid={`envio-${x.envio.clave}`}
      className={`flex min-w-0 items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold leading-tight ${MUNDO[c.mundo].cls} ${
        x.envio.proxima ? "border border-dashed border-current" : ""
      } hover:ring-1 hover:ring-violet-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400`}
    >
      <IconosCanal canales={c.canales} size={11} />
      <span className="min-w-0 truncate">
        {x.dia.hora && <span className="tabular-nums">{x.dia.hora} </span>}
        {c.nombre || "Sin nombre"}
      </span>
      {x.dia.cantidad !== null && <span className="ml-auto shrink-0 font-normal tabular-nums">{num(x.dia.cantidad)}</span>}
      <Estado size={11} className={`shrink-0 ${x.dia.cantidad === null ? "ml-auto" : ""}`} aria-hidden="true" />
      {marcada && <AlertTriangle size={11} className="shrink-0 text-amber-600" aria-hidden="true" />}
    </Link>
  );
}

/** Una campaña en la semana o en la lista por día: el nombre, su tipo y su estado, la hora y cuántas personas. */
function TarjetaEnvio({ x, marcada, equipo }: { x: EnvioDelDia; marcada: boolean; equipo: boolean }) {
  const c = x.envio.campana;
  const estado = x.envio.proxima ? PROXIMA : ESTADO[c.estado];
  const sale = x.envio.proxima || c.estado === "programada";
  const cuando = [x.dia.hora ? `${sale ? "Sale a las" : "Desde las"} ${x.dia.hora}` : null, textoDelDia(x)].filter(Boolean).join(" · ");
  return (
    <Link
      to={c.link}
      data-testid={`envio-${x.envio.clave}`}
      className={`block min-h-[44px] min-w-0 space-y-1 rounded-xl border bg-white p-2.5 hover:border-violet-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
        x.envio.proxima ? "border-dashed border-violet-300" : marcada ? "border-amber-300" : "border-slate-200"
      }`}
    >
      <p className="flex items-start gap-1.5 break-words text-sm font-semibold text-slate-900">
        {marcada && <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />}
        <span className="min-w-0">
          {c.nombre || "Sin nombre"}
          {x.envio.proxima && <span className="font-normal text-slate-500"> (próxima repetición)</span>}
        </span>
      </p>
      <span className="flex flex-wrap gap-1">
        <span className={`${chip} ${MUNDO[c.mundo].cls}`}>
          <IconosCanal canales={c.canales} size={10} />
          {MUNDO[c.mundo].label}
        </span>
        <span className={`${chip} ${estado.cls}`} data-testid="estado">
          {estado.texto}
        </span>
        {c.simulada && <span className={`${chip} bg-amber-50 text-amber-800`}>Simulada</span>}
      </span>
      {cuando && <span className="block text-xs text-slate-600">{cuando}</span>}
      {c.parteDe && equipo && <span className="block break-words text-xs text-slate-500">Es el envío de «{c.parteDe.nombre || "su campaña con segmentación"}»</span>}
      {marcada && <span className="block text-xs text-amber-800">Ojo: {CERCA}.</span>}
    </Link>
  );
}

interface PropsGrilla {
  rango: RangoVista;
  dias: Map<string, EnvioDelDia[]>;
  hoy: string;
  marcadas: ReadonlySet<string>;
}

function GrillaMes({ rango, dias, mes, hoy, marcadas, verSemana }: PropsGrilla & { mes: number; verSemana: (dia: string) => void }) {
  return (
    <div className="hidden md:grid grid-cols-7 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200" data-testid="grilla-mes">
      {DIAS_SEMANA.map((d) => (
        <div key={d} className="bg-slate-50 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500" aria-hidden="true">
          {d}
        </div>
      ))}
      {rango.dias.map((d) => {
        const xs = dias.get(d) ?? [];
        const fuera = deClave(d)?.getMonth() !== mes;
        const resto = xs.length - MAX_EN_CELDA;
        return (
          <section key={d} aria-label={capital(tituloDelDia(d))} data-testid={`dia-${d}`} className={`min-h-[120px] min-w-0 space-y-1 p-1.5 ${fuera ? "bg-slate-50" : "bg-white"}`}>
            <NumeroDelDia dia={d} hoy={hoy} apagado={fuera} />
            {xs.slice(0, MAX_EN_CELDA).map((x) => (
              <ChipEnvio key={x.envio.clave} x={x} marcada={marcadas.has(x.envio.clave)} />
            ))}
            {resto > 0 && (
              <button
                type="button"
                onClick={() => verSemana(d)}
                className="min-h-[44px] w-full rounded-md text-xs font-semibold text-violet-700 hover:bg-violet-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                +{num(resto)} más
                <span className="sr-only"> el {tituloDelDia(d)}: ver la semana</span>
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}

function GrillaSemana({ rango, dias, hoy, marcadas, equipo }: PropsGrilla & { equipo: boolean }) {
  return (
    <div className="hidden md:grid grid-cols-7 gap-2" data-testid="grilla-semana">
      {rango.dias.map((d, i) => {
        const xs = dias.get(d) ?? [];
        return (
          <section key={d} aria-label={capital(tituloDelDia(d))} data-testid={`dia-${d}`} className="min-w-0 space-y-2">
            <p className="flex items-center gap-1.5 border-b border-slate-200 pb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {DIAS_SEMANA[i]} <NumeroDelDia dia={d} hoy={hoy} />
            </p>
            {xs.length ? (
              xs.map((x) => <TarjetaEnvio key={x.envio.clave} x={x} marcada={marcadas.has(x.envio.clave)} equipo={equipo} />)
            ) : (
              <p className="text-xs text-slate-400">—</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** En el celular: un renglón por día (del mes, los que tienen algo; de la semana, los siete) con sus campañas. */
function ListaPorDia({
  dias,
  deCadaDia,
  hoy,
  marcadas,
  equipo,
  vacio,
}: {
  dias: string[];
  deCadaDia: Map<string, EnvioDelDia[]>;
  hoy: string;
  marcadas: ReadonlySet<string>;
  equipo: boolean;
  vacio: string;
}) {
  if (!dias.length) {
    return (
      <p className="text-sm text-slate-500 md:hidden" data-testid="lista-vacia">
        {vacio}
      </p>
    );
  }
  return (
    <ol className="space-y-4 md:hidden" aria-label="Día por día" data-testid="lista-por-dia">
      {dias.map((d) => {
        const xs = deCadaDia.get(d) ?? [];
        return (
          <li key={d} data-testid={`renglon-${d}`}>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              {capital(tituloDelDia(d))}
              {d === hoy && <span className={`${chip} bg-violet-600 text-white`}>Hoy</span>}
            </h3>
            {xs.length ? (
              <ul className="mt-2 space-y-2">
                {xs.map((x) => (
                  <li key={x.envio.clave}>
                    <TarjetaEnvio x={x} marcada={marcadas.has(x.envio.clave)} equipo={equipo} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-slate-400">Nada programado.</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
